import React, { useEffect, useState } from "react";
import { ApplicationConfigModal, ApplicationConfigs } from "common/types";
import { Button, Modal } from "semantic-ui-react";
import ConfigForm from "components/ConfigForm";
import { ConfigStatus, FILE_STORAGE_API_ENDPOINT } from "common/constants";
import ErrorModalState from "components/ErrorModalState";
import InternalServerErrorState from "components/InternalServerErrorState";
import NotFoundState from "components/NotFoundState";
import _ from "lodash";
import SuccessState from "components/SuccessState";
import { connect } from "react-redux";
import { apiToken } from "services/legit/token";
import { RootState } from "StoreTypes";
import { HTTPStatusCodes } from "types";
import Spinner from "../Spinner";
import ApexLogin from "../../containers/ApexLogin";
import { get, post, patch, destroy } from "../../utils/requests";
import styles from "./styles.module.scss";

export interface Props {
  token: string | null;
  configDetail: ApplicationConfigModal;
  isVisible: boolean;
  handleClose: (event: React.SyntheticEvent<{}>) => void;
  handleSubmit: () => void;
}

const initialState: ApplicationConfigs = {
  config: {
    accepted_mime_types: "",
    pubsub_topic: "",
    max_file_size: "",
    read_permission: "",
    write_permission: "",
    bucket_name: "",
  },
  config_version: 0,
  application_name: "",
  deleted: false,
  status: ConfigStatus.Active,
};
const DataModal = ({
  handleSubmit,
  token,
  configDetail,
  isVisible,
  handleClose,
}: Props) => {
  const [selectedRecord, setSelectedRecord] =
    useState<ApplicationConfigModal>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [create, setCreate] = useState(false);
  const [success, setSuccess] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState(false);
  const [internalServerError, setInternalServerError] = useState(false);
  const [preconditionFail, setPreconditionFail] = useState(false);
  const [headers] = useState(
    new Headers({
      "Content-Type": "application/json",
      Authorization: token as string,
    })
  );

  useEffect(() => {
    if (_.isEmpty(configDetail)) {
      setCreate(true);
      setLoading(false);
      setUpdate(false);
      setSuccess(false);
    } else {
      fetchConfig();
      setUpdate(false);
      setSuccess(false);
    }
  }, [configDetail]);

  function fetchConfig() {
    setLoading(true);
    const endpoint = `${FILE_STORAGE_API_ENDPOINT}/${configDetail.id}`;
    get(endpoint, headers)
      .then((result) => {
        setSelectedRecord(result);
        setCreate(false);
        setLoading(false);
        setError(false);
        setSuccess(false);
      })
      .catch((error) => {
        if (
          error.response?.status ===
          HTTPStatusCodes.STATUS_INTERNAL_SERVER_ERROR
        ) {
          setInternalServerError(true);
          setLoading(false);
          setError(true);
          setCreate(false);
        } else if (
          error.response?.status === HTTPStatusCodes.STATUS_NOT_FOUND
        ) {
          setNotFound(true);
          setLoading(false);
          setError(true);
          setCreate(false);
        } else {
          setError(true);
          setLoading(false);
          setCreate(false);
        }
      });
  }

  function addAppConfig(app: any) {
    const request = JSON.stringify(app);
    const endpoint = `${FILE_STORAGE_API_ENDPOINT}`;
    post(endpoint, request, headers, true)
      .then((res) => {
        setSuccess(true);
        handleSubmit();
      })
      .catch((error) => {
        switch (error.response?.status) {
          case HTTPStatusCodes.STATUS_INTERNAL_SERVER_ERROR:
            setInternalServerError(true);
            setNotFound(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          case HTTPStatusCodes.STATUS_NOT_FOUND:
            setNotFound(true);
            setInternalServerError(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          case HTTPStatusCodes.STATUS_PRECONDITION_FAILED:
            setNotFound(false);
            setInternalServerError(false);
            setSuccess(false);
            setPreconditionFail(true);
            setError(true);
            break;
          default:
            setError(true);
            setSuccess(false);
        }
      });
  }

  function updateAppConfig(app: any) {
    const request = JSON.stringify(app);
    const endpoint = `${FILE_STORAGE_API_ENDPOINT}/${app.id}`;
    patch(endpoint, request, headers, true)
      .then((res) => {
        setSuccess(true);
        setUpdate(true);
        handleSubmit();
      })
      .catch((error) => {
        switch (error.response?.status) {
          case HTTPStatusCodes.STATUS_INTERNAL_SERVER_ERROR:
            setInternalServerError(true);
            setNotFound(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          case HTTPStatusCodes.STATUS_NOT_FOUND:
            setNotFound(true);
            setInternalServerError(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          case HTTPStatusCodes.STATUS_PRECONDITION_FAILED:
            setNotFound(false);
            setInternalServerError(false);
            setSuccess(false);
            setPreconditionFail(true);
            setError(true);
            break;
          default:
            setError(true);
            setSuccess(false);
        }
      });
  }

  function deleteAppConfig(app: any) {
    const request = JSON.stringify(app);
    const endpoint = `${FILE_STORAGE_API_ENDPOINT}/${app.id}`;
    destroy(endpoint, request, true)
      .then((res) => {
        setSuccess(true);
        handleSubmit();
      })
      .catch((error) => {
        switch (error.response?.status) {
          case HTTPStatusCodes.STATUS_INTERNAL_SERVER_ERROR:
            setInternalServerError(true);
            setNotFound(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          case HTTPStatusCodes.STATUS_NOT_FOUND:
            setNotFound(true);
            setInternalServerError(false);
            setSuccess(false);
            setPreconditionFail(false);
            setError(true);
            break;

          default:
            setError(true);
            setSuccess(false);
        }
      });
  }

  const renderErrorState = () => {
    if (internalServerError) {
      return <InternalServerErrorState />;
    }
    if (preconditionFail) {
      return <ApexLogin />;
    }
    if (notFound) {
      return <NotFoundState />;
    }
    return <ErrorModalState />;
  };

  const renderHeader = () => {
    if (create) {
      return "Create";
    }
    if (error) {
      return "Error";
    }
    return configDetail.application_name;
  };

  function updateConfig() {
    setUpdate(true);
  }

  return (
    <Spinner loading={loading}>
      <Modal onClose={handleClose} open={isVisible}>
        <Modal.Header
          id={`modal-header-${configDetail.application_name}`}
          className={styles.header}
        >
          <>{renderHeader()}</>
          {!_.isEmpty(configDetail) && !success && (
            <div>
              <Button
                className={styles.archive}
                basic
                id="delete-btn"
                color="red"
                icon="trash"
                content="Delete"
                onClick={() => deleteAppConfig(configDetail)}
              />
              <Button
                basic
                id="update-btn"
                color="green"
                icon="pencil"
                content="Edit"
                onClick={() => updateConfig()}
              />
            </div>
          )}
        </Modal.Header>
        {error ? (
          <>{renderErrorState()}</>
        ) : (
          <Modal.Content>
            {success && <SuccessState />}
            {_.isEmpty(configDetail) && (
              <ConfigForm
                config={null}
                create={initialState}
                disabled={false}
                handleSubmit={addAppConfig}
                update={update}
              />
            )}
            {selectedRecord && !create && (
              <ConfigForm
                config={selectedRecord}
                create={initialState}
                handleSubmit={update ? updateAppConfig : addAppConfig}
                disabled
                update={update}
              />
            )}
          </Modal.Content>
        )}
        <Modal.Actions>
          <Button color="black" id="close-modal" onClick={handleClose}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </Spinner>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: apiToken(state),
});

export default connect(mapStateToProps)(DataModal);
